import {
  PromotionPrerequisiteIssue,
  PromotionPrerequisiteIssueCode,
} from '../../../services/BaseBackendService';
import { STATUS_LABELS } from './constants';

export function fundWorkflowStatusDisplayName(status: string): string {
  return STATUS_LABELS[status] ?? status.replace(/_/g, ' ');
}

/**
 * Verb phrase mid-sentence: "Are you sure you want to _____?"
 * Mirrors backend `replay.operation` segments on `/investors/:id/…`.
 */
export function investorFundStatusOperationVerbPhrase(operation: string | undefined): string {
  switch (operation) {
    case 'promoteToLead':
      return 'promote this investor to Lead';
    case 'submitEOI':
      return 'submit an Expression of Interest for this investor';
    case 'promoteToOpportunity':
      return 'promote this investor to Opportunity';
    case 'commitInvestment':
      return 'record a committed investment for this investor';
    case 'demoteToOpportunity':
      return 'demote this investor to Opportunity';
    case 'demoteToLead':
      return 'demote this investor to Lead';
    case 'demoteToCandidate':
      return 'demote this investor to Candidate';
    case 'withdrawEOI':
      return "withdraw this investor's EOI";
    default:
      return 'perform this investor action';
  }
}

export function fundStatusInvestorOverrideLeadSentence(
  issues: PromotionPrerequisiteIssue[],
): string | undefined {
  const row = issues.find((i) => i.issue === 'FUND_STATUS_REQUIRES_OVERRIDE');
  const fundStatus = row?.context?.fundStatus?.trim();
  if (!fundStatus) {
    return undefined;
  }
  const readable = fundWorkflowStatusDisplayName(fundStatus);
  const action = investorFundStatusOperationVerbPhrase(row?.context?.operation);
  return `This fund is in ${readable} status. Are you sure you want to ${action}?`;
}

export function shortPromotionIssueLabel(issue: PromotionPrerequisiteIssueCode): string {
  switch (issue) {
    case 'MISSING_CONTENT':
      return 'Published content is required.';
    case 'MISSING_PDF':
      return 'No investor PDF.';
    case 'MISSING_SUMMARY_IMAGE':
      return 'No summary/hero image selected in published content.';
    case 'MISSING_IRR_UPDATE':
      return 'No initial cashflow report uploaded.';
    case 'MISSING_COMMITTED_INVESTMENTS':
      return 'No committed investor capital.';
    case 'FUND_STATUS_REQUIRES_OVERRIDE':
      return 'Fund workflow normally blocks this investor action.';
    default:
      return issue;
  }
}

export function promotionIssueDisplayText(row: PromotionPrerequisiteIssue): string {
  if (row.issue === 'FUND_STATUS_REQUIRES_OVERRIDE') {
    if (row.context?.fundStatus?.trim()) {
      const readable = fundWorkflowStatusDisplayName(row.context.fundStatus);
      const action = investorFundStatusOperationVerbPhrase(row.context.operation);
      return `This fund is in ${readable} status. Are you sure you want to ${action}?`;
    }
    return shortPromotionIssueLabel(row.issue);
  }
  return row.message?.trim() ? row.message : shortPromotionIssueLabel(row.issue);
}
